import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
// adjust path
import useApi from "../hooks/useApi";
import { getMenuAdminList, getMenuItemById, getMenuList } from "../services/adminApiService/menuApi/MenuApiService";

const MenuManagementContext = createContext();

export const MenuManagementProvider = ({ children }) => {

  const { loading, error, request } = useApi();
  const [menuDataList, setMenuDataList] = useState([]);
  const [sideabarmenuDataList, setSidebarMenuDataList] = useState([]);
  const [menuListById, setMenuListById] = useState([]);
  const [menuId, setMenuId] = useState([]);
  const [sidebarLoading, setSidebarLoading] = useState(false);

  const fetchMenuList = useCallback(async (perPage, currentPage) => {

    try {
      const response = await request(() =>
        getMenuAdminList(perPage, currentPage)
      );
      setMenuDataList(response?.data || []);
    } catch (err) {
      console.log("Menu List Error:", err);
    }
  }, [request]);


  const DESIRED_ORDER = [
    "Dashboard",
    "Udiyamaan Unnayan Yojna",
    "Protsaahan",
    "Academy Management",
    "Attendance Management",
    "Communication Hub",
    "Content Management",
    "Customer Bookings",
    "DIGI ACCRED",
    "Edit Profile Request",
    "Event Management",
    "Event Ticketing",
    "Grant Schemes",
    "Grievance Management",
    "Hostel Management",
    "Inventory Management",
    "Learning Management",
    "Manage Registration",
    "Manage Verification",
    "Menu Management",
    "Notification Management",
    "Project Management",
    "Scheme Management",
    "Service Provider",
    "Ticket Management",
    "Ticketing Dashboard",
    "User Management",
    "Venue Booking",
    "Venue Dashboard",
    "Venue Infra",
    "Venue Manager Dashboard",
  ];

  const getMenuOrder = (title) => {
    const lower = title?.toLowerCase();
    // Exact match first — prevents "Ticketing Dashboard" stealing "Dashboard" position
    const exactIdx = DESIRED_ORDER.findIndex((k) => lower === k.toLowerCase());
    if (exactIdx !== -1) return exactIdx;
    // Fallback: partial match for DB titles that differ slightly (e.g. "Venue Manager Dashboard" → "Venue Dashboard")
    const idx = DESIRED_ORDER.findIndex((k) => lower?.includes(k.toLowerCase()));
    return idx === -1 ? DESIRED_ORDER.length : idx;
  };

  const fetchSidebarMenuList = useCallback(async () => {

    try {
      setSidebarLoading(true);
      const response = await request(getMenuList);
      const menuList = response?.data?.data || [];

      const sortedMenuList = [...menuList].sort(
        (a, b) => getMenuOrder(a.title) - getMenuOrder(b.title)
      );

      // Fallback menu injections below are only valid for full/unrestricted admins.
      // Restricted roles (district/block/nyaya-panchayat, user_level 3/6/7) must show
      // exactly what the backend's role-based menu filtering returned — injecting
      // these nodes unconditionally would grant sidebar access (and, via
      // permissionRoutes below) real route access to modules the role was never given.
      const loginDetails = JSON.parse(localStorage.getItem("login_Details"));
      const userLevel = loginDetails?.user?.user_level;
      const isUnrestrictedAdmin = userLevel === 2;

      // Inject Academy Dashboard under Academy Management if not already in DB
      const academyMenu = sortedMenuList.find((item) =>
        item.title?.toLowerCase().includes("academy management")
      );
      if (isUnrestrictedAdmin && academyMenu && Array.isArray(academyMenu.children)) {
        // Fix any existing item with wrong URL
        academyMenu.children.forEach((child) => {
          if (child.title === "Academy Dashboard" && child.url !== "batch-management/dashboard") {
            child.url = "batch-management/dashboard";
            child.is_externalUrl = false;
          }
        });

        const alreadyExists = academyMenu.children.some(
          (child) =>
            child.url?.replace(/^\/+/, "") === "batch-management/dashboard" ||
            child.title === "Academy Dashboard"
        );
        if (!alreadyExists) {
          academyMenu.children.unshift({
            title: "Academy Dashboard",
            url: "batch-management/dashboard",
            is_externalUrl: false,
            sort_order: 0,
            children: [],
          });
        }

        // Inject Attendance tab if not already present
        const attendanceExists = academyMenu.children.some(
          (child) =>
            child.url?.replace(/^\/+/, "") === "batch-management/attendance-report" ||
            child.title === "Attendance"
        );
        if (!attendanceExists) {
          academyMenu.children.push({
            title: "Attendance",
            url: "batch-management/attendance-report",
            is_externalUrl: false,
            sort_order: 99,
            children: [],
          });
        }
      }

      // Inject Content Management > Government Orders if not already in DB
      const contentMgmtIndex = sortedMenuList.findIndex((item) =>
        item.title?.toLowerCase().includes("content management")
      );
      if (isUnrestrictedAdmin && contentMgmtIndex === -1) {
        sortedMenuList.push({
          title: "Content Management",
          url: null,
          is_externalUrl: false,
          icon: "fas fa-file-alt",
          sort_order: DESIRED_ORDER.indexOf("Content Management"),
          children: [
            {
              title: "Government Orders",
              url: "content/government-orders",
              is_externalUrl: false,
              sort_order: 0,
              children: [],
            },
            {
              title: "Circulars",
              url: "content/circulars",
              is_externalUrl: false,
              sort_order: 1,
              children: [],
            },
          ],
        });
        // Re-sort after injection
        sortedMenuList.sort((a, b) => getMenuOrder(a.title) - getMenuOrder(b.title));
      } else if (contentMgmtIndex !== -1) {
        // Content Management exists (from DB, role legitimately has it) — ensure Circulars child is present
        const children = sortedMenuList[contentMgmtIndex].children || [];
        const circularsExists = children.some((c) =>
          c.url === "content/circulars"
        );
        if (!circularsExists) {
          sortedMenuList[contentMgmtIndex].children = [
            ...children,
            {
              title: "Circulars",
              url: "content/circulars",
              is_externalUrl: false,
              sort_order: children.length,
              children: [],
            },
          ];
        }
      }

      // ✅ Extract only URLs
      const permissionRoutes = [
        ...new Set(
          sortedMenuList
            .flatMap((item) =>
              (item.children || []).map((child) =>
                child.url?.replace(/^\//, "")   // remove only starting '/'
              )
            )
            .filter((url) => url && url.trim() !== "")
        )
      ];


      // ✅ Store in localStorage
      localStorage.setItem("permission_route_url", JSON.stringify(permissionRoutes));
      setSidebarMenuDataList({ ...response?.data, data: sortedMenuList });
    } catch (err) {
      console.log("Menu List Error:", err);
    } finally {
      setSidebarLoading(false);  // stop loader
    }
  }, [request]);

  const fetchMenuListById = useCallback(async (menuId) => {
    try {
      const response = await request(() => getMenuItemById(menuId));
      setMenuListById(response?.data?.data || []);
    } catch (err) {
      console.log("Menu List Error:", err);
    }
  }, [request]);



  return (
    <MenuManagementContext.Provider
      value={{
        loading,
        error,
        menuDataList,
        fetchSidebarMenuList,
        sideabarmenuDataList,
        fetchMenuList,
        fetchMenuListById,
        menuListById,
        setMenuId,
        menuId,
        sidebarLoading
      }}
    >
      {children}
    </MenuManagementContext.Provider>
  );
};

export const useMenuManagement = () => {
  return useContext(MenuManagementContext);
};